import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Plus, X, Star, Loader2, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_PHOTOS = 8;

const PhotoUpload = ({ photos = [], profilePhoto, onUpdate }) => {
  const [uploading, setUploading] = useState(false);
  const [deletingPhoto, setDeletingPhoto] = useState(null);
  const fileInputRef = useRef(null);

  const getPhotoUrl = (photoPath) => {
    if (!photoPath) return null;
    if (photoPath.startsWith('http')) return photoPath;
    // Handle both /uploads/ (old) and /api/photos/ (new) paths
    if (photoPath.startsWith('/uploads/')) {
      // Convert old path to new API path
      const filename = photoPath.replace('/uploads/', '');
      return `${BACKEND_URL}/api/photos/${filename}`;
    }
    // Already an API path or relative path
    return `${BACKEND_URL}${photoPath}`;
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type — accept anything image-like on mobile
    const isImage = file.type.startsWith('image/') || /\.(jpe?g|png|webp|heic|heif|gif)$/i.test(file.name);
    if (!isImage) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (10MB max for mobile photos)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/photos/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Photo uploaded!');
      onUpdate?.();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to upload photo';
      toast.error(message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async (photoPath) => {
    const filename = photoPath.split('/').pop();
    setDeletingPhoto(filename);

    try {
      await axios.delete(`${API}/photos/${filename}`);
      toast.success('Photo deleted');
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to delete photo');
    } finally {
      setDeletingPhoto(null);
    }
  };

  const handleSetPrimary = async (photoPath) => {
    const filename = photoPath.split('/').pop();

    try {
      await axios.put(`${API}/photos/set-primary/${filename}`);
      toast.success('Profile photo updated');
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to set profile photo');
    }
  };

  const isPrimary = (photoPath) => {
    return photoPath === profilePhoto;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium">Photos</h3>
          <p className="text-sm text-muted-foreground">
            {photos.length} of {MAX_PHOTOS} photos
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {/* Existing photos */}
        {photos.map((photo, index) => (
          <Card
            key={photo}
            className={`relative aspect-square overflow-hidden rounded-xl group ${
              isPrimary(photo) ? 'ring-2 ring-primary' : ''
            }`}
          >
            <img
              src={getPhotoUrl(photo)}
              alt={`Photo ${index + 1}`}
              className="w-full h-full object-cover"
            />
            
            {/* Primary badge */}
            {isPrimary(photo) && (
              <div className="absolute top-2 left-2 bg-primary text-primary-foreground px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1">
                <Star className="w-3 h-3 fill-current" />
                Main
              </div>
            )}

            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              {!isPrimary(photo) && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => handleSetPrimary(photo)}
                  className="h-8 text-xs"
                  data-testid={`set-primary-${index}`}
                >
                  <Star className="w-3 h-3 mr-1" />
                  Set Main
                </Button>
              )}
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleDelete(photo)}
                disabled={deletingPhoto === photo.split('/').pop()}
                className="h-8 w-8 p-0"
                data-testid={`delete-photo-${index}`}
              >
                {deletingPhoto === photo.split('/').pop() ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <X className="w-4 h-4" />
                )}
              </Button>
            </div>
          </Card>
        ))}

        {/* Add photo button */}
        {photos.length < MAX_PHOTOS && (
          <Card
            className="aspect-square rounded-xl border-2 border-dashed border-border hover:border-primary/50 transition-colors cursor-pointer flex flex-col items-center justify-center gap-2"
            onClick={() => fileInputRef.current?.click()}
            data-testid="add-photo-btn"
          >
            {uploading ? (
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            ) : (
              <>
                <Plus className="w-6 h-6 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Add Photo</span>
              </>
            )}
          </Card>
        )}

        {/* Empty slots */}
        {[...Array(Math.max(0, 3 - photos.length - 1))].map((_, i) => (
          <Card
            key={`empty-${i}`}
            className="aspect-square rounded-xl border border-dashed border-border/50 flex items-center justify-center"
          >
            <ImageIcon className="w-6 h-6 text-muted-foreground/30" />
          </Card>
        ))}
      </div>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/*"
        className="hidden"
        data-testid="photo-input"
      />

      <p className="text-xs text-muted-foreground">
        Tap a photo to set it as your main profile picture. Maximum 5MB per photo.
      </p>
    </div>
  );
};

export default PhotoUpload;
